import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailCheck, ShieldCheck } from "lucide-react";
import { API } from "@/lib/config";
import { inputCls, Field, GoldButton, Honeypot, PrivacyNote } from "./fields";
import { errorMessage } from "./useMember";

const PENDING_KEY = "cc_pending_email";

const CONSENT_TEXT = "I agree to receive emails from Captain's Cut. I can unsubscribe at any time.";

/** purpose "community" = joining the crew email list (consent required); "application" = transactional only (consent optional). */
export const VerifyEmail = ({ onVerified, intro, purpose = "community" }) => {
  const [email, setEmail] = useState(() => localStorage.getItem(PENDING_KEY) || "");
  const [sent, setSent] = useState(() => !!localStorage.getItem(PENDING_KEY));
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [showConsentError, setShowConsentError] = useState(false);
  const consentRequired = purpose === "community";

  const requestCode = async (e) => {
    e?.preventDefault();
    if (consentRequired && !consent) { setShowConsentError(true); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/members/request-code`, { email, purpose, marketing_consent: consent, website });
      localStorage.setItem(PENDING_KEY, email);
      setSent(true);
      toast.success(`Verification code sent to ${email}`);
    } catch (err) {
      toast.error(errorMessage(err, "Could not send the code — try again."));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/members/verify-code`, { email, code });
      localStorage.removeItem(PENDING_KEY);
      onVerified(data.token, data.member);
    } catch (err) {
      toast.error(errorMessage(err, "Incorrect code."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="verify-email-step">
      <div className="flex items-start gap-3 border border-[#D4AF37]/25 bg-[#070A0F] p-4">
        <ShieldCheck size={18} className="text-[#E8C15A] shrink-0 mt-0.5" />
        <p className="text-xs text-[#94A3B8] leading-relaxed">
          {intro || "Verify your email to join the crew. We'll send a 6-digit code — no password needed."}
        </p>
      </div>
      {!sent ? (
        <form onSubmit={requestCode} className="space-y-4 mt-5">
          <Field label="Email address">
            <input required type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
              className={inputCls} data-testid="verify-email-input" />
          </Field>
          <Honeypot value={website} onChange={(e) => setWebsite(e.target.value)} />
          <label className="flex items-start gap-3 cursor-pointer group" data-testid="consent-label">
            <input type="checkbox" checked={consent} onChange={(e) => { setConsent(e.target.checked); if (e.target.checked) setShowConsentError(false); }}
              className="mt-0.5 h-4 w-4 shrink-0 appearance-none border border-[#D4AF37]/60 bg-[#070A0F] checked:bg-[#D4AF37] checked:border-[#D4AF37] cursor-pointer transition-colors"
              data-testid="consent-checkbox" />
            <span className="text-xs text-[#94A3B8] leading-relaxed group-hover:text-[#F5F3EF] transition-colors">
              {CONSENT_TEXT}{!consentRequired && <span className="text-[#94A3B8]/60"> (optional)</span>}
            </span>
          </label>
          {showConsentError && (
            <p className="text-[11px] text-[#F5B7B1]" data-testid="consent-error">Please tick the box to join the crew email list.</p>
          )}
          <PrivacyNote>We use your email to send a verification code{consentRequired ? " and, with your consent, crew updates" : ""}.</PrivacyNote>
          <GoldButton type="submit" disabled={busy || (consentRequired && !consent)} data-testid="verify-send-code-btn">
            {busy ? "SENDING…" : "SEND VERIFICATION CODE"}
          </GoldButton>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-4 mt-5">
          <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
            <MailCheck size={14} className="text-[#E8C15A]" /> Code sent to <span className="text-[#F5F3EF]" data-testid="verify-sent-to">{email}</span>
          </div>
          <Field label="6-digit code" hint="expires in 15 minutes">
            <input required inputMode="numeric" pattern="\d{6}" maxLength={6} placeholder="••••••" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className={`${inputCls} font-coords text-2xl tracking-[0.6em] text-center`} data-testid="verify-code-input" />
          </Field>
          <GoldButton type="submit" disabled={busy || code.length !== 6} data-testid="verify-submit-code-btn">
            {busy ? "CHECKING…" : "VERIFY EMAIL"}
          </GoldButton>
          <div className="flex justify-between text-[11px] tracking-[0.15em] uppercase">
            <button type="button" onClick={() => { setSent(false); localStorage.removeItem(PENDING_KEY); }}
              className="text-[#94A3B8] hover:text-[#E8C15A] transition-colors" data-testid="verify-change-email-btn">
              Change email
            </button>
            <button type="button" onClick={requestCode} disabled={busy}
              className="text-[#E8C15A] hover:text-[#F3E08A] transition-colors disabled:opacity-50" data-testid="verify-resend-btn">
              Resend code
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
